import React from 'react';
import { OptionsPageShell } from '../../components/options/OptionsPageShell';
import OptionsLoadingState from '../../components/options/OptionsLoadingState';
import { OptionsOnboardingCard } from '../../components/options/OptionsOnboardingCard';
import { OptionsPerformanceAccountSummaryCompact } from '../../components/options/OptionsPerformanceAccountSummaryCompact';
import { OptionsPerformanceControls } from '../../components/options/OptionsPerformanceControls';
import { OptionsPerformanceEquityChartCard } from '../../components/options/OptionsPerformanceEquityChartCard';
import { OptionsPerformanceEquityProjectionCard } from '../../components/options/OptionsPerformanceEquityProjectionCard';
import { OptionsCapitalPeriodsSection } from '../../components/options/OptionsCapitalPeriodsSection';
import OptionsCapitalSummarySection from '../../components/options/OptionsCapitalSummarySection';
import { useOptionsOverviewState } from '../../hooks/useOptionsOverviewState';
import { useOptionsPerformanceData } from '../../hooks/useOptionsPerformanceData';
import { isPreviewRuntime } from '../../lib/runtime';
import { useOptionsMode } from '../../context/OptionsModeContext';
import { useOptionsLocale } from '../../context/OptionsLanguageContext';
import { useOptionsEntitlements } from '../../context/OptionsEntitlementsContext';
import { OptionsProLockedFeature } from '../../components/options/OptionsProLockedFeature';
import {
  buildCapitalPeriodSections,
  buildEquityProjectionModel,
  buildPerformanceChartModel,
  buildPerformanceAccountSummaryModel,
  getChartTitle,
} from '../../components/options/optionsPerformanceUtils';

export default function OptionsPerformance() {
  const { isDemoMode } = useOptionsMode();
  const { t } = useOptionsLocale();
  const { entitlements } = useOptionsEntitlements();
  const { connected, loading: overviewLoading, reloadOverview, overview } = useOptionsOverviewState();
  const {
    accountData,
    performanceData,
    historyData,
    capitalPeriodsData,
    capitalPeriodsAllData,
    currentCapitalPeriodsData,
    loading,
    rangeFetching,
    syncing,
    selectedCurrency,
    setSelectedCurrency,
    selectedRange,
    setSelectedRange,
    customRange,
    setCustomRange,
    handleApplyCustomRange,
    handleSync,
  } = useOptionsPerformanceData(connected, reloadOverview);

  const showTestnetBadge = isPreviewRuntime() && accountData?.environment === 'testnet';
  const accountSummary = buildPerformanceAccountSummaryModel({
    accountData,
    performanceData,
    capitalPeriodsData,
    currentCapitalPeriodsData,
    historyData,
    selectedCurrency,
    t,
    selectedRange,
    customRange,
  });
  const chartModel = buildPerformanceChartModel({ historyData, capitalPeriodsData, selectedCurrency, t });
  const capitalPeriodSections = buildCapitalPeriodSections({ capitalPeriodsData, currentCapitalPeriodsData, selectedCurrency, t });
  const equityProjection = buildEquityProjectionModel({ currentCapitalPeriodsData, selectedCurrency });

  return (
    <OptionsPageShell
      title={t('performance.title')}
      description={t('performance.description')}
      eyebrow={t('performance.eyebrow')}
      hideHeader
    >
      {overviewLoading ? (
        <OptionsLoadingState
          title={t('common.connectDeribit')}
          description={t('performance.connectDescription')}
          testId="options-performance-loading-overview"
        />
      ) : !connected ? (
        <OptionsOnboardingCard
          title={t('onboarding.title')}
          description={t('onboarding.description')}
          connectLabel={t('common.connectDeribit')}
          securityTitle={t('onboarding.securityTitle')}
          securityBody={t('onboarding.securityBody')}
          securityDetails={t('onboarding.securityDetails')}
        />
      ) : loading ? (
        <OptionsLoadingState
          title={t('common.syncingNow')}
          description={t('performance.description')}
          testId="options-performance-loading-state"
        />
      ) : (
        <div className="space-y-6" data-testid="options-performance-root">
          <OptionsPerformanceControls
            selectedCurrency={selectedCurrency}
            onCurrencyChange={setSelectedCurrency}
            selectedRange={selectedRange}
            onRangeChange={setSelectedRange}
            customRange={customRange}
            onCustomRangeChange={setCustomRange}
            onApplyCustomRange={handleApplyCustomRange}
            onSync={handleSync}
            syncing={syncing}
            showTestnetBadge={showTestnetBadge}
            showSyncButton={!isDemoMode && overview?.can_sync !== false}
            isFetching={rangeFetching}
          />

          <OptionsPerformanceAccountSummaryCompact model={accountSummary} />

          {entitlements.ACCOUNT_EVOLUTION ? (
            <OptionsPerformanceEquityChartCard title={getChartTitle(selectedCurrency, t)} chartModel={chartModel} selectedCurrency={selectedCurrency} />
          ) : (
            <OptionsProLockedFeature featureKey="PERFORMANCE" title={t('performance.equityTitle') || 'Evolução da Conta'} description="Acompanhe a curva histórica de equity e sua evolução por período." testId="options-performance-equity-pro-lock" />
          )}

          {entitlements.EQUITY_PROJECTION ? (
            <OptionsPerformanceEquityProjectionCard projection={equityProjection} selectedCurrency={selectedCurrency} />
          ) : (
            <OptionsProLockedFeature featureKey="PERFORMANCE" title="Projeção de Equity" description="Projete a evolução do patrimônio com base na performance observada." testId="options-performance-projection-pro-lock" />
          )}

          {entitlements.CAPITAL_PERIODS ? (
            <OptionsCapitalPeriodsSection sections={capitalPeriodSections} />
          ) : (
            <OptionsProLockedFeature featureKey="PERFORMANCE" title="Períodos de Capital" description="Analise retornos entre aportes e retiradas sem misturar fluxos de caixa com resultado operacional." testId="options-performance-capital-periods-pro-lock" />
          )}

          <OptionsCapitalSummarySection
            capitalPeriodsAllData={capitalPeriodsAllData}
            accountData={accountData}
            selectedCurrency={selectedCurrency}
            t={t}
          />
        </div>
      )}
    </OptionsPageShell>
  );
}